import { AsyncPipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PageChangeEvent } from '@progress/kendo-angular-grid';
import { Observable, combineLatest, map, shareReplay } from 'rxjs';
import { IAssignment } from 'src/app/assignment/models/assignment';
import { IAssignmentService } from 'src/app/assignment/models/assignment-service';
import { IBaseSearchRequest, IGridState, PageSize } from 'src/app/core/models/base';
import { IEmployee } from 'src/app/employee/employee';
import { IDayReportingObject } from 'src/app/time-tracking/models/day-reporting-object';
import { ISearchTimeEntriesRequest, ITimeEntry } from 'src/app/time-tracking/models/time-entry';
import { ITimeEntryCategory } from 'src/app/time-tracking/models/time-entry-category';
import { TimeEntryCategoryService } from 'src/app/time-tracking/services/time-entry-category.service';
import { TimeEntryService } from 'src/app/time-tracking/services/time-entry.service';
import { TimeEntryAccessService, TimeEntryWriteAction } from 'src/app/time-tracking/services/time-entry-access.service';
import { TimeSpan } from '../helpers/time-span';
import { masketTextBoxValidator } from '../validators/masketTextBox.validator';
import { StopTimeValidator } from '../validators/stopTime.validator';
import { AppStateInterface } from 'src/app/stores/global/appState.interface';
import { Store } from '@ngrx/store';
import { currentEmployeeSelector } from 'src/app/stores/global/current-employee/current-employee.store';
import { getEmployees } from 'src/app/stores/global/employees/employees.store';
import { PermissionStorageService } from 'src/app/authentication/storages/permission-storage.service';

@UntilDestroy()
@Component({
    selector: 'app-time-entry-grid',
    templateUrl: './time-entry-grid.component.html',
    styleUrls: ['./time-entry-grid.component.css'],
    standalone: false
})
export class TimeEntryGridComponent implements OnInit {

  constructor(
    private timeEntryService: TimeEntryService,
    private timeEntryCategoryService : TimeEntryCategoryService,
    private accessService: TimeEntryAccessService,
    private asyncPipe: AsyncPipe,
    private permissionStorage: PermissionStorageService,
    private store: Store<AppStateInterface>  )
    {
      this.store.dispatch(getEmployees());
    }

  @Input() public timeEntries: ITimeEntry[];
  @Input() public searchRequest: ISearchTimeEntriesRequest;
  @Input() public dayReportingObject: IDayReportingObject;
  @Input() public timeEntryCategories: ITimeEntryCategory[] = [];
  @Input() public assignment: IAssignment;
  @Input() public isAdminView: boolean = false;
  @Input() public isEditable: boolean = true;
  @Input() public isDayReportView: boolean = false;
  // For the views that list one's own entries only: a new entry is always for oneself, even if
  // the user could create for others - it would not show up in the list it was added to.
  @Input() public ownEntriesOnly: boolean = false;
  // Total over the rows of the current page.
  @Input() public set totalTime(totalTime: string) {
    if(totalTime){
      this.totalTimeObj = TimeSpan.parse(totalTime);
    }
  }
  // Grand total over the whole selection (all pages), shown as a second footer line.
  @Input() public set grandTotalTime(grandTotalTime: string) {
    if(grandTotalTime){
      this.grandTotalTimeObj = TimeSpan.parse(grandTotalTime);
    }
  }
  @Output() onUpdateGrid: EventEmitter<undefined> = new EventEmitter();

  totalTimeObj: TimeSpan;
  grandTotalTimeObj: TimeSpan;

  get hasGrandTotal(): boolean {
    return !!this.grandTotalTimeObj;
  }

  public formGroup!: UntypedFormGroup;
  private editedRowIndex!: number;

  public currentEmployee$ : Observable<IEmployee> =
  this.store.select(currentEmployeeSelector);

  // Ids of the employees (other than oneself) each write right reaches - resolved by the server
  // per action, so that the buttons only show where a save would succeed.
  private writableIds: Record<TimeEntryWriteAction, Set<string>> = {
    Create: new Set<string>(),
    Update: new Set<string>(),
    Delete: new Set<string>()
  };

  // Choices for a new entry: oneself with CreateSelf, plus whoever the server lets the user
  // create for (subordinates with Create, everyone with CreateAll). Exactly the set the server
  // would accept, so no dead choices in the dropdown.
  public employees$ : Observable<IEmployee[]> = combineLatest([
    this.currentEmployee$,
    this.accessService.writableEmployees.Create
  ]).pipe(
    map(([current, writable]) => [
      ...(current && this.hasPermission('Permissions.TimeEntries.CreateSelf') ? [current] : []),
      ...(this.ownEntriesOnly ? [] : writable)
    ]),
    shareReplay({ bufferSize: 1, refCount: false })
  );

  mask = "00:00";
  editMask = "AB:CD";
  maskRules: { [key: string]: RegExp } = {
    A: /[0-2]+/,
    B: /[0-9]+/,
    C: /[0-5]+/,
    D: /[0-9]+/,
  };

  gridState: IGridState = {
    pageSize: 25,
    skip: 0,
  };

  defaultItem: IAssignmentService = {
    id: null,
    remarks: "Bitte wählen ...",
    isCompleted: false,
    created_at: null
  }

  get pageSizes() {
    return PageSize;
  }

  // Writing follows the server rule: one's own entry takes the Self variant (zeit.manuell),
  // a subordinate's the plain right (zeit.startstopp), anybody else's the All variant (zeit.alle).
  // Neither the plain right nor the All variant covers oneself.
  private hasPermission(permission: string): boolean {
    return (this.permissionStorage.getPermissions() ?? []).includes(permission);
  }

  private canWrite(action: TimeEntryWriteAction, employeeId: string): boolean {
    const currentId = this.asyncPipe.transform(this.currentEmployee$)?.id;
    return employeeId === currentId
      ? this.hasPermission(`Permissions.TimeEntries.${action}Self`)
      : this.writableIds[action].has(employeeId);
  }

  // The add button only shows when there is somebody to create an entry for. A right alone is
  // not enough: Create without subordinates leaves the employee dropdown empty.
  canAdd = false;

  canEdit(timeEntry: ITimeEntry): boolean {
    return this.canWrite('Update', timeEntry.employeeId);
  }

  canRemove(timeEntry: ITimeEntry): boolean {
    return this.canWrite('Delete', timeEntry.employeeId);
  }

  ngOnInit(): void {
    // Only the rows need the update and delete reach; the create reach feeds the dropdown.
    (['Update', 'Delete'] as TimeEntryWriteAction[]).forEach(action =>
      this.accessService.writableEmployees[action]
        .pipe(untilDestroyed(this))
        .subscribe(employees => this.writableIds[action] = new Set(employees.map(e => e.id))));

    this.employees$
      .pipe(untilDestroyed(this))
      .subscribe(employees => this.canAdd = employees.length > 0);

    if(this.timeEntryCategories.length == 0){
      this.timeEntryCategoryService.searchTimeEntryCategories()
      .pipe(untilDestroyed(this))
      .subscribe(response => {
        this.timeEntryCategories = response.data
      })
    }
  }

  createFormGroup(
    dataItem: ITimeEntry = null, 
    isNew = true, 
    date : Date = null, 
    assignment : IAssignment = null)
  {

    // Preselect oneself only when an own entry may be created - otherwise the default would be
    // a choice the dropdown does not even offer.
    var employee = dataItem?.employee != null
      ? dataItem.employee
      : this.dayReportingObject?.employee != null
        ? this.dayReportingObject?.employee
        : this.hasPermission('Permissions.TimeEntries.CreateSelf')
          ? this.asyncPipe.transform(this.currentEmployee$)
          : null

    var date = dataItem?.date != null ? dataItem.date : date;

          return new UntypedFormGroup({
      date: new UntypedFormControl(new Date(date), Validators.required),
      start: new UntypedFormControl(dataItem?.start, {validators: [Validators.required,masketTextBoxValidator("  :  ")]}),
      stop: new UntypedFormControl(dataItem?.stop, {validators: [Validators.required, masketTextBoxValidator("  :  ")]}),
      remarks: new UntypedFormControl(dataItem?.remarks),
      employee: new UntypedFormControl({value: employee, disabled: !isNew}, Validators.required),
      timeEntryCategory: new UntypedFormControl(dataItem?.timeEntryCategory, Validators.required),
      assignment: new UntypedFormControl(dataItem?.assignment ?? assignment),
      assignmentService: new UntypedFormControl(dataItem?.assignmentService),
    },{validators:[StopTimeValidator("start", "stop")], updateOn: "change"});
  }

  addHandler({ sender }){
    this.closeEditor(sender);

    this.formGroup = this.createFormGroup(null, true, this.dayReportingObject?.date ? new Date(this.dayReportingObject?.date) : new Date(), this.assignment);

    this.onAssignmentChange();
    sender.addRow(this.formGroup);
  }

  editHandler({ sender, rowIndex, dataItem }){
    this.closeEditor(sender);

    this.formGroup = this.createFormGroup(dataItem, false)

    this.editedRowIndex = rowIndex;

    this.onAssignmentChange();
    sender.editRow(rowIndex, this.formGroup);
  }

  public cancelHandler({ sender, rowIndex }) {
    this.closeEditor(sender, rowIndex);
  }

  public saveHandler(event) {
    const request: any = {
      date: new Date(event.formGroup.value.date).toDateString(),
      start: TimeSpan.parse(event.formGroup.value.start).toString(),
      stop: event.formGroup.value.stop ? TimeSpan.parse(event.formGroup.value.stop).toString() : null,
      remarks: event.formGroup.value.remarks,
      timeEntryCategoryId: event.formGroup.value.timeEntryCategory.id,
      id: event.dataItem.id ?? null,
      employeeId: event.formGroup.value.employee?.id,
      assignmentId: event.formGroup.value.assignment?.id,
      assignmentServiceId: event.formGroup.value.assignmentService?.id,
    }
    if (!event.dataItem.id){
      this.timeEntryService.createTimeEntry(request)
      .pipe(untilDestroyed(this))
      .subscribe(response => {
        this.onUpdateGrid.emit();
      });
    } else {
      this.timeEntryService.updateTimeEntry(request)
      .pipe(untilDestroyed(this))
      .subscribe(response => {
        this.onUpdateGrid.emit();
      });;
    }

    event.sender.closeRow(event.rowIndex);
    
  }

  private closeEditor(grid, rowIndex = this.editedRowIndex) {
    grid.closeRow(rowIndex);
    this.editedRowIndex = undefined;
    this.formGroup = undefined;
  }

  public removeHandler({dataItem}) {
    // remove the current dataItem from the current data source,
    // `editService` in this example
    this.timeEntryService.deleteTimeEntry(dataItem.id)
    .pipe(untilDestroyed(this))
    .subscribe(response => {
      this.onUpdateGrid.emit();
    });;
  }

  onPageSizeChange(pageSize: number) {
    this.onPageChange({ skip: this.gridState.skip, take: pageSize });
  }

  onGridReload() {
    this.onUpdateGrid.emit();
  }

  onPageChange({ skip, take }: PageChangeEvent) {
    const page = Math.round((skip + take) / take);

    this.gridState.skip = skip;
    this.gridState.pageSize = take;

    this.searchRequest.pageNumber = page;
    this.searchRequest.pageSize = take;

    this.onUpdateGrid.emit();
  }

  get startIsLargerThanStop() {
    return (
      this.formGroup.getError('startIsLargerThanStop') &&
      this.formGroup.get('stop')?.touched
    );
  }

  onAssignmentChange (){
    this.formGroup.get('assignment').valueChanges
    .pipe(untilDestroyed(this))
    .subscribe(value => {
      this.formGroup.get('assignmentService').patchValue(null);
    })
  }



}
